import { Controller } from "@hotwired/stimulus";
import { enqueueHarvestEntry } from "../../db/harvestEntries";
import { checkOnlineStatus } from "../../onlineStatus";
import { farmDatetimeLocalNow } from "../../helpers/farm_time";
import * as Turbo from "@hotwired/turbo";

// Connects to data-controller="harvest-entries--without-vehicle-form"
//
// The no-vehicle flow pre-fills net_weight as harvest_units * harvest_unit.capacity_kg
// (capacity is server-rendered as data-capacity-kg on each harvest_unit option).
// The user can override net_weight; once they edit it, the override flag is set
// and the auto-recompute stops firing for the rest of this page life.
//
// When offline, the form's submit is intercepted, validated client-side, and
// the payload is enqueued in IndexedDB for replay when the device comes back online.
export default class extends Controller {
  static targets = [
    "entryAt",
    "harvestUnitSelect",
    "harvestUnits",
    "netWeightValue",
    "overrideFlag",
  ];

  static values = {
    farmId: Number,
    timeZone: String,
    indexUrl: String,
    errorRequired: String,
    errorGreaterThanZero: String,
  };

  connect() {
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    this.element.addEventListener("submit", (e) => this.#handleSubmit(e), {
      signal,
    });

    this.#defaultEntryAtToNow();
  }

  disconnect() {
    this.#abortController.abort();
  }

  recompute() {
    if (this.#isOverridden()) return;

    const capacityKg = this.#selectedCapacityKg();
    const units = parseInt(this.harvestUnitsTarget?.value, 10);

    if (!capacityKg || !units || units <= 0) return;

    if (!this.hasNetWeightValueTarget) return;
    this.netWeightValueTarget.value = (capacityKg * units).toFixed(2);
  }

  markOverridden() {
    if (!this.hasOverrideFlagTarget) return;
    this.overrideFlagTarget.value = "true";
  }

  #abortController;
  #submittingOnline = false;

  #isOverridden() {
    return this.hasOverrideFlagTarget && this.overrideFlagTarget.value === "true";
  }

  #selectedCapacityKg() {
    if (!this.hasHarvestUnitSelectTarget) return null;
    const opt = this.harvestUnitSelectTarget.selectedOptions[0];
    if (!opt) return null;
    const cap = parseFloat(opt.dataset.capacityKg);
    return isNaN(cap) ? null : cap;
  }

  #defaultEntryAtToNow() {
    if (!this.hasEntryAtTarget || this.entryAtTarget.value) return;

    this.entryAtTarget.value = farmDatetimeLocalNow(this.timeZoneValue);
  }

  async #handleSubmit(event) {
    if (this.#submittingOnline) return;
    const submitter = event.submitter;
    if (!submitter || submitter.name !== "commit") return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (await checkOnlineStatus()) {
      this.#submittingOnline = true;
      this.element.requestSubmit(submitter);
      return;
    }

    this.#clearErrors();
    const errors = this.#validate();

    if (errors.length > 0) {
      this.#showErrors(errors);
      return;
    }

    this.#saveOffline(submitter.value);
  }

  #validate() {
    const errors = [];
    const val = (name) =>
      this.element
        .querySelector(`[name="harvest_entry[${name}]"]`)
        ?.value?.trim();

    if (!val("entry_at"))
      errors.push({ field: "entry_at", message: this.errorRequiredValue });
    if (!val("lot_id"))
      errors.push({ field: "lot_id", message: this.errorRequiredValue });
    if (!val("harvest_group_id"))
      errors.push({
        field: "harvest_group_id",
        message: this.errorRequiredValue,
      });
    if (!val("harvest_unit_id"))
      errors.push({
        field: "harvest_unit_id",
        message: this.errorRequiredValue,
      });

    const harvestUnits = parseInt(val("harvest_units"));
    if (!harvestUnits || harvestUnits <= 0) {
      errors.push({
        field: "harvest_units",
        message: this.errorGreaterThanZeroValue,
      });
    }

    const netWeight = parseFloat(val("net_weight_value"));
    if (!netWeight || netWeight <= 0) {
      errors.push({
        field: "net_weight_value",
        message: this.errorGreaterThanZeroValue,
      });
    }

    return errors;
  }

  #showErrors(errors) {
    const targets = errors.map(({ field, message }) => {
      const input = this.element.querySelector(
        `[name="harvest_entry[${field}]"]`
      );
      const wrapper = input?.closest(".fieldset");
      return { input, wrapper, message };
    });

    for (const { input, wrapper, message } of targets) {
      if (!input || !wrapper) continue;
      const errorEl = document.createElement("p");
      errorEl.className = "text-sm text-error mt-1 js-offline-error";
      errorEl.textContent = message;
      wrapper.appendChild(errorEl);
      input.classList.add("input-error");
    }
  }

  #clearErrors() {
    this.element
      .querySelectorAll(".js-offline-error")
      .forEach((el) => el.remove());
    this.element
      .querySelectorAll(".input-error")
      .forEach((el) => el.classList.remove("input-error"));
  }

  async #saveOffline(commitValue) {
    const formData = new FormData(this.element);
    const payload = {};
    for (const [key, value] of formData.entries()) {
      payload[key] = value;
    }
    payload["commit"] = commitValue;

    const display = this.#extractDisplayData();

    try {
      await enqueueHarvestEntry(this.farmIdValue, payload, display);
    } catch (err) {
      console.error("[harvest-entries--without-vehicle-form] Failed to save offline:", err);
      return;
    }

    sessionStorage.setItem("harvestEntryOfflineFlash", "1");
    Turbo.visit(this.indexUrlValue);
  }

  #extractDisplayData() {
    const elements = {};
    const fields = [
      "entry_at", "lot_id", "harvest_group_id", "harvest_unit_id",
      "harvest_units", "net_weight_value", "net_weight_unit",
      "scale_ticket_code", "field_statement_code",
    ];
    for (const name of fields) {
      elements[name] = this.element.querySelector(
        `[name="harvest_entry[${name}]"]`
      );
    }

    const val = (name) => elements[name]?.value?.trim() || "";
    const selectText = (name) =>
      elements[name]?.selectedOptions?.[0]?.text || "";

    const netVal = parseFloat(val("net_weight_value")) || 0;
    const units = parseInt(val("harvest_units")) || 1;
    const avgVal = units > 0 ? netVal / units : 0;
    const entryAt = val("entry_at");
    const unit = val("net_weight_unit") || "kg";

    return {
      entry_at: entryAt ? new Date(entryAt).toLocaleDateString(document.documentElement.lang || undefined) : "",
      lot_name: selectText("lot_id"),
      harvest_group_name: selectText("harvest_group_id"),
      harvest_unit_name: selectText("harvest_unit_id"),
      net_weight: `${netVal.toFixed(2)} ${unit}`,
      average_harvest_unit_weight: `${avgVal.toFixed(2)} ${unit}`,
      harvest_units: val("harvest_units"),
      scale_ticket_code: val("scale_ticket_code"),
      field_statement_code: val("field_statement_code"),
    };
  }
}
